import { useState, useEffect, useRef, useCallback } from 'react';
import { resolverImagen } from '../hooks/useApi';
import './Admin.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// ── Configuración de secciones ───────────────────────────────────
const SECTIONS = [
  {
    id: 'pokemon', label: 'Pokédex', rune: '📖',
    endpoint: '/api/pokemon',
    getName: p => `${p.id} ${p.nombre}`,
    getThumb: p => p.imagen,
    hasBadge: p => !p.imagen,
    badgeText: 'Sin imagen',
    toForm: p => ({
      nombre: p.nombre || '', pokeid: p.id || '',
      tipos: (p.tipos || []).join(', '),
      habilidades: (p.habilidades || []).join(', '),
      descripcion: p.descripcion || '',
      ps:  p.stats?.ps  ?? '', atq: p.stats?.atq ?? '',
      def: p.stats?.def ?? '', spa: p.stats?.spa ?? '',
      spd: p.stats?.spd ?? '', vel: p.stats?.vel ?? '',
    }),
    buildBody: f => ({
      nombre: f.nombre, id: f.pokeid,
      tipos: f.tipos.split(',').map(t => t.trim()).filter(Boolean),
      habilidades: f.habilidades.split(',').map(h => h.trim()).filter(Boolean),
      descripcion: f.descripcion,
      ps: Number(f.ps), atq: Number(f.atq), def: Number(f.def),
      spa: Number(f.spa), spd: Number(f.spd), vel: Number(f.vel),
    }),
    fields: [
      { key: 'nombre',      label: 'Nombre',        type: 'text' },
      { key: 'pokeid',      label: 'ID (#001)',      type: 'text' },
      { key: 'tipos',       label: 'Tipos',          type: 'text', hint: 'separados por coma' },
      { key: 'habilidades', label: 'Habilidades',    type: 'text', hint: 'separadas por coma' },
      { key: 'descripcion', label: 'Descripción',    type: 'textarea' },
      {
        group: 'Stats', cols: 3, fields: [
          { key: 'ps',  label: 'PS',       type: 'number' },
          { key: 'atq', label: 'Ataque',   type: 'number' },
          { key: 'def', label: 'Defensa',  type: 'number' },
          { key: 'spa', label: 'At. Esp.', type: 'number' },
          { key: 'spd', label: 'Def.Esp.', type: 'number' },
          { key: 'vel', label: 'Veloc.',   type: 'number' },
        ],
      },
    ],
  },
  {
    id: 'lore', label: 'Crónicas', rune: '📜',
    endpoint: '/api/lore',
    getName: l => l.titulo || '(sin título)',
    getThumb: l => l.imagen,
    hasBadge: () => false,
    toForm: l => ({
      titulo: l.titulo || '', lineaTemporal: l.lineaTemporal || '',
      sujetos: l.sujetos || '', estado: l.estado || '', desarrollo: l.desarrollo || '',
    }),
    buildBody: f => ({ titulo: f.titulo, linea: f.lineaTemporal, sujetos: f.sujetos, estado: f.estado, desarrollo: f.desarrollo }),
    fields: [
      { key: 'titulo',        label: 'Título',         type: 'text' },
      { key: 'lineaTemporal', label: 'Línea temporal', type: 'text' },
      { key: 'sujetos',       label: 'Sujetos',        type: 'text' },
      { key: 'estado',        label: 'Estado',         type: 'text' },
      { key: 'desarrollo',    label: 'Desarrollo',     type: 'textarea' },
    ],
  },
  {
    id: 'personajes', label: 'Personajes', rune: '⚔️',
    endpoint: '/api/personajes',
    getName: p => p.nombre || '(sin nombre)',
    getThumb: p => p.imagen,
    hasBadge: () => false,
    toForm: p => ({
      nombre: p.nombre || '', clasificacion: p.clasificacion || '',
      rasgos: p.rasgos || '', motivacion: p.motivacion || '',
      equipo: (p.equipo || []).join(', '), historia: p.historia || '',
    }),
    buildBody: f => ({
      nombre: f.nombre, clasificacion: f.clasificacion, rasgos: f.rasgos,
      motivacion: f.motivacion, equipo: f.equipo, historia: f.historia,
    }),
    fields: [
      { key: 'nombre',        label: 'Nombre',        type: 'text' },
      { key: 'clasificacion', label: 'Clasificación', type: 'text' },
      { key: 'rasgos',        label: 'Rasgos',        type: 'text' },
      { key: 'motivacion',    label: 'Motivación',    type: 'textarea' },
      { key: 'equipo',        label: 'Equipo Pokémon', type: 'text', hint: 'separados por coma' },
      { key: 'historia',      label: 'Historia',      type: 'textarea' },
    ],
  },
  {
    id: 'arte', label: 'Galería', rune: '🖼️',
    endpoint: '/api/arte',
    getName: a => a.nombre || '(sin título)',
    getThumb: a => a.imagen,
    hasBadge: () => false,
    toForm: a => ({
      nombre: a.nombre || '', categoria: a.categoria || '', autor: a.autor || '',
      estado: a.estado || '', inspiracion: a.inspiracion || '',
      uso: a.uso || '', version: a.version || '',
    }),
    buildBody: f => ({ nombre: f.nombre, categoria: f.categoria, autor: f.autor, estado: f.estado, inspiracion: f.inspiracion, uso: f.uso, version: f.version }),
    fields: [
      { key: 'nombre',      label: 'Nombre',       type: 'text' },
      { key: 'categoria',   label: 'Categoría',    type: 'text' },
      { key: 'autor',       label: 'Autor',        type: 'text' },
      { key: 'estado',      label: 'Estado',       type: 'text' },
      { key: 'inspiracion', label: 'Inspiración',  type: 'textarea' },
      { key: 'uso',         label: 'Uso',          type: 'text' },
      { key: 'version',     label: 'Versión',      type: 'text' },
    ],
  },
  {
    id: 'progreso', label: 'Progreso', rune: '📊',
    endpoint: '/api/progreso',
    singleEntry: true,
    getName: () => 'Progreso',
    getThumb: () => null,
    hasBadge: () => false,
    toForm: p => ({ pokedex: p.pokedex || '', lore: p.lore || '', mecanica: p.mecanica || '', notas: p.notas || '' }),
    buildBody: f => ({ pokedex: f.pokedex, lore: f.lore, mecanica: f.mecanica, notas: f.notas }),
    putEndpoint: '/api/progreso',
    fields: [
      { key: 'pokedex',  label: 'Pokédex',  type: 'text' },
      { key: 'lore',     label: 'Lore',     type: 'text' },
      { key: 'mecanica', label: 'Mecánica', type: 'text' },
      { key: 'notas',    label: 'Notas',    type: 'textarea' },
    ],
  },
];

// ── Login Gate ───────────────────────────────────────────────────
function LoginGate({ onLogin }) {
  const [pass,    setPass]    = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!pass.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/api/admin/verify`, {
        headers: { 'x-api-key': pass.trim() },
      });
      if (res.ok) {
        onLogin(pass.trim());
      } else {
        setError('Contraseña incorrecta.');
      }
    } catch {
      setError('No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login">
      <div className="admin-login-box">
        <div className="admin-login-rune">◈</div>
        <h2 className="admin-login-title">Panel de Administración</h2>
        <p className="admin-login-sub">Introduce la contraseña para continuar</p>
        <form className="admin-login-form" onSubmit={handleSubmit}>
          <input
            className="admin-login-input"
            type="password"
            placeholder="Contraseña…"
            value={pass}
            onChange={e => setPass(e.target.value)}
            autoFocus
          />
          {error && <p className="admin-login-error">{error}</p>}
          <button className="admin-login-btn" type="submit" disabled={loading || !pass}>
            {loading ? 'Verificando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────
export default function Admin() {
  const [apiKey,   setApiKey]   = useState(() => localStorage.getItem('admin_api_key') || '');
  const [authed,   setAuthed]   = useState(() => !!localStorage.getItem('admin_api_key'));
  const [section,  setSection]  = useState(SECTIONS[0]);
  const [items,    setItems]    = useState([]);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState({});
  const [search,   setSearch]   = useState('');
  const [imgUrl,   setImgUrl]   = useState('');
  const [imgModo,  setImgModo]  = useState('url');
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const fileRef = useRef(null);

  function handleLogin(key) {
    localStorage.setItem('admin_api_key', key);
    setApiKey(key);
    setAuthed(true);
  }

  function handleLogout() {
    localStorage.removeItem('admin_api_key');
    setApiKey('');
    setAuthed(false);
  }

  // Cargar items al cambiar de sección
  const loadItems = useCallback(async () => {
    setItems([]);
    setSelected(null);
    setForm({});
    setMsg(null);
    try {
      const res  = await fetch(`${BASE}${section.endpoint}`);
      const data = await res.json();
      if (section.singleEntry) {
        // Progreso: cargar directamente en el form
        setForm(section.toForm(data || {}));
        setSelected(data || {});
      } else {
        setItems(Array.isArray(data) ? data : []);
      }
    } catch {
      setMsg({ type: 'err', text: 'Error al cargar datos.' });
    }
  }, [section]);

  useEffect(() => { if (authed) loadItems(); }, [authed, loadItems]);

  function selectItem(item) {
    setSelected(item);
    setForm(section.toForm(item));
    setImgUrl('');
    setMsg(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function updateField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  // Guardar datos del formulario (PUT)
  async function saveData() {
    if (!selected) return;
    setSaving(true);
    setMsg(null);
    try {
      const endpoint = section.singleEntry
        ? `${BASE}${section.putEndpoint || section.endpoint}`
        : `${BASE}${section.endpoint}/${selected._id}`;
      const method = section.singleEntry ? 'PUT' : 'PUT';
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify(section.buildBody(form)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      if (!section.singleEntry) {
        setItems(prev => prev.map(p => p._id === data._id ? data : p));
        setSelected(data);
        setForm(section.toForm(data));
      }
      setMsg({ type: 'ok', text: 'Guardado correctamente.' });
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  // Actualizar imagen via URL
  async function updateImgUrl() {
    if (!imgUrl.trim() || !selected?._id) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${BASE}${section.endpoint}/${selected._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify({ imagen: imgUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setItems(prev => prev.map(p => p._id === data._id ? data : p));
      setSelected(data);
      setImgUrl('');
      setMsg({ type: 'ok', text: 'Imagen actualizada.' });
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  // Actualizar imagen via archivo
  async function updateImgFile() {
    const file = fileRef.current?.files?.[0];
    if (!file || !selected?._id) return;
    setSaving(true);
    setMsg(null);
    try {
      const form = new FormData();
      form.append('imagen', file);
      const res = await fetch(`${BASE}${section.endpoint}/${selected._id}/imagen`, {
        method: 'POST',
        headers: { 'x-api-key': apiKey },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setItems(prev => prev.map(p => p._id === data._id ? data : p));
      setSelected(data);
      if (fileRef.current) fileRef.current.value = '';
      setMsg({ type: 'ok', text: 'Imagen actualizada.' });
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  const hasImage = section.id !== 'progreso';
  const filtered = items.filter(it =>
    section.getName(it).toLowerCase().includes(search.toLowerCase())
  );

  if (!authed) return <LoginGate onLogin={handleLogin} />;

  return (
    <div className="admin-page">
      {/* ── Header ── */}
      <header className="admin-header">
        <span className="admin-header-title">⚙️ Admin</span>
        <nav className="admin-tabs">
          {SECTIONS.map(s => (
            <button
              key={s.id}
              className={`admin-tab${section.id === s.id ? ' admin-tab--active' : ''}`}
              onClick={() => { setSection(s); setSearch(''); }}
            >
              <span>{s.rune}</span> {s.label}
            </button>
          ))}
        </nav>
        <button className="admin-logout" onClick={handleLogout}>Salir</button>
      </header>

      {/* ── Mensaje ── */}
      {msg && (
        <div className={`admin-msg admin-msg--${msg.type}`}>
          {msg.text}
          <button className="admin-msg-close" onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {/* ── Cuerpo ── */}
      <div className="admin-body">

        {/* Lista lateral (no mostrar para entradas únicas) */}
        {!section.singleEntry && (
          <aside className="admin-list-panel">
            <input
              className="admin-search"
              type="text"
              placeholder={`Buscar en ${section.label}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <ul className="admin-list">
              {filtered.map(item => (
                <li
                  key={item._id}
                  className={`admin-list-item${selected?._id === item._id ? ' admin-list-item--active' : ''}${section.hasBadge(item) ? ' admin-list-item--pending' : ''}`}
                  onClick={() => selectItem(item)}
                >
                  <span className="admin-item-thumb">
                    {section.getThumb(item)
                      ? <img src={resolverImagen(section.getThumb(item))} alt="" />
                      : <span className="admin-item-placeholder">?</span>
                    }
                  </span>
                  <span className="admin-item-name">{section.getName(item)}</span>
                  {section.hasBadge(item) && <span className="admin-item-badge">Sin imagen</span>}
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Editor */}
        <main className={`admin-editor${section.singleEntry ? ' admin-editor--full' : ''}`}>
          {(!selected && !section.singleEntry) ? (
            <div className="admin-empty">Selecciona un elemento de la lista.</div>
          ) : (
            <div className="admin-form-wrap">
              {!section.singleEntry && (
                <h2 className="admin-form-title">{section.getName(selected)}</h2>
              )}

              {/* Campos */}
              {section.fields.map((field, i) => {
                if (field.group) {
                  return (
                    <fieldset key={i} className="admin-fieldset">
                      <legend className="admin-fieldset-legend">{field.group}</legend>
                      <div className={`admin-fields-grid admin-fields-grid--${field.cols || 2}`}>
                        {field.fields.map(f => (
                          <div key={f.key} className="admin-field">
                            <label className="admin-label">{f.label}</label>
                            <input
                              className="admin-input"
                              type="number"
                              value={form[f.key] ?? ''}
                              onChange={e => updateField(f.key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </fieldset>
                  );
                }
                return (
                  <div key={field.key} className="admin-field">
                    <label className="admin-label">
                      {field.label}
                      {field.hint && <span className="admin-hint"> · {field.hint}</span>}
                    </label>
                    {field.type === 'textarea'
                      ? <textarea className="admin-input admin-textarea" value={form[field.key] ?? ''} onChange={e => updateField(field.key, e.target.value)} rows={4} />
                      : <input className="admin-input" type={field.type} value={form[field.key] ?? ''} onChange={e => updateField(field.key, e.target.value)} />
                    }
                  </div>
                );
              })}

              <button className="admin-btn" onClick={saveData} disabled={saving}>
                {saving ? 'Guardando…' : '💾 Guardar cambios'}
              </button>

              {/* Imagen (no para Progreso) */}
              {hasImage && selected?._id && (
                <div className="admin-img-section">
                  <h3 className="admin-img-title">Imagen</h3>
                  <div className="admin-card-img-wrap">
                    {section.getThumb(selected)
                      ? <img src={resolverImagen(section.getThumb(selected))} alt="" className="admin-card-img" />
                      : <div className="admin-card-img-placeholder">Sin imagen</div>
                    }
                  </div>
                  <div className="admin-modo-toggle">
                    <button className={`admin-modo-btn${imgModo === 'url' ? ' admin-modo-btn--active' : ''}`} onClick={() => setImgModo('url')}>URL externa</button>
                    <button className={`admin-modo-btn${imgModo === 'archivo' ? ' admin-modo-btn--active' : ''}`} onClick={() => setImgModo('archivo')}>Subir archivo</button>
                  </div>
                  {imgModo === 'url' ? (
                    <div className="admin-img-row">
                      <input className="admin-input" type="url" placeholder="https://…" value={imgUrl} onChange={e => setImgUrl(e.target.value)} />
                      <button className="admin-btn admin-btn--sm" onClick={updateImgUrl} disabled={saving || !imgUrl}>
                        {saving ? '…' : 'Actualizar'}
                      </button>
                    </div>
                  ) : (
                    <div className="admin-img-row">
                      <input ref={fileRef} className="admin-input admin-input--file" type="file" accept="image/*" />
                      <button className="admin-btn admin-btn--sm" onClick={updateImgFile} disabled={saving}>
                        {saving ? '…' : 'Subir'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
